import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface ApiErrorRow {
  error_id: string;
  account_id?: string | null;
  platform?: string | null;
  endpoint: string;
  http_status?: number | null;
  reason?: string | null;
  detail?: string | null;
  created_at: string;
  extra?: Record<string, unknown>;
}

/**
 * Recent API errors card — surfaces failures from external social APIs
 * (YouTube, Meta, LinkedIn, etc.) with per-platform filtering and a purge
 * button. Records auto-expire after `retention_days` days (default 7).
 */
export const ApiErrorsCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [platformFilter, setPlatformFilter] = useState("");

  const q = useQuery<{ errors: ApiErrorRow[]; count: number; retention_days: number }>({
    queryKey: ["admin-api-errors", platformFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (platformFilter) params.set("platform", platformFilter);
      const { data } = await api.get(`/admin/api-errors?${params.toString()}`);
      return data;
    },
    refetchInterval: 30_000,
  });

  const purge = useMutation({
    mutationFn: async () => (await api.delete("/admin/api-errors")).data,
    onSuccess: (d) => {
      toast.success(`Purged ${d.deleted} error rows.`);
      qc.invalidateQueries({ queryKey: ["admin-api-errors"] });
      qc.invalidateQueries({ queryKey: ["admin-health"] });
    },
    onError: (e: any) => toast.error("Purge failed: " + (e?.response?.data?.detail || e.message)),
  });

  const platforms = React.useMemo(() => {
    const s = new Set<string>();
    (q.data?.errors ?? []).forEach((e) => e.platform && s.add(e.platform));
    return Array.from(s).sort();
  }, [q.data]);

  return (
    <div
      data-testid="admin-api-errors-card"
      className="bg-white rounded-xl p-6 border border-[color:var(--sg-border)] mt-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <Activity className="h-4 w-4 text-[color:var(--sg-violet)]" />
        <h2 className="text-lg font-semibold text-[color:var(--sg-fg)]">Recent API Errors</h2>
      </div>
      <p className="text-sm text-[color:var(--sg-muted)] mb-3">
        Errors from external social APIs (YouTube, Meta, LinkedIn, etc.). Records auto-delete after{" "}
        <b>{q.data?.retention_days ?? 7} days</b>.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          data-testid="api-errors-platform-filter"
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="text-xs border border-[color:var(--sg-border)] rounded-md px-2 py-1 bg-white"
        >
          <option value="">All platforms</option>
          {platforms.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="text-[11px] text-[color:var(--sg-muted)]">
          {q.data?.count ?? 0} record{(q.data?.count ?? 0) === 1 ? "" : "s"}
        </div>
        <div className="flex-1" />
        {!readonly && (
          <Button
            size="sm"
            variant="outline"
            data-testid="api-errors-purge"
            disabled={purge.isPending || (q.data?.count ?? 0) === 0}
            onClick={() => {
              if (window.confirm("Delete all recorded API errors for this workspace?")) {
                purge.mutate();
              }
            }}
            className="border-red-200 text-red-700 hover:bg-red-50 h-8"
          >
            {purge.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 mr-1" />
            )}
            Clear all
          </Button>
        )}
      </div>

      {q.isLoading ? (
        <div className="sg-skeleton h-32" />
      ) : (q.data?.errors ?? []).length === 0 ? (
        <div className="text-xs text-[color:var(--sg-muted)] text-center py-6 border border-dashed border-[color:var(--sg-border)] rounded-md">
          No API errors in the last 7 days. Everything is healthy.
        </div>
      ) : (
        <div className="border border-[color:var(--sg-border)] rounded-md overflow-hidden">
          <div className="grid grid-cols-[110px_90px_90px_1fr_60px] gap-0 text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)] bg-[color:var(--sg-muted-bg)] px-3 py-1.5 border-b border-[color:var(--sg-border)]">
            <div>Time</div>
            <div>Platform</div>
            <div>Status</div>
            <div>Reason / Endpoint / Detail</div>
            <div className="text-right">HTTP</div>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-[color:var(--sg-border)]">
            {q.data!.errors.map((err) => (
              <div
                key={err.error_id}
                data-testid={`api-error-row-${err.error_id}`}
                className="grid grid-cols-[110px_90px_90px_1fr_60px] gap-0 items-start px-3 py-2 text-[11px] hover:bg-slate-50"
              >
                <div className="text-[color:var(--sg-muted)]">
                  {new Date(err.created_at).toLocaleString(undefined, {
                    month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </div>
                <div className="font-medium text-[color:var(--sg-fg)]">{err.platform || "—"}</div>
                <div>
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      (err.http_status ?? 0) >= 500
                        ? "bg-red-100 text-red-800"
                        : (err.http_status ?? 0) >= 400
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {err.reason || "error"}
                  </span>
                </div>
                <div className="min-w-0 text-[color:var(--sg-fg)]">
                  <div className="font-mono text-[10px] text-[color:var(--sg-muted)] truncate">
                    {err.endpoint}
                  </div>
                  {err.detail && (
                    <div className="text-[11px] mt-0.5 line-clamp-2">{err.detail}</div>
                  )}
                </div>
                <div className="text-right font-mono text-[color:var(--sg-muted)]">
                  {err.http_status ?? "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
